// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { mdiDelete, mdiPencil } from "@mdi/js";
import {
  Button,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tooltip
} from "antd";
import type { ColumnsType } from "antd/es/table";
import styled from "styled-components";

import useCanUseGeoModels from "hooks/useCanUseGeoModels";
import { useLengthConversion } from "hooks/useUnitConversion";
import useUserModules from "hooks/useUserModules";

import { PlayRadiusDefault, PlayRadiusDimension } from "api/ipdp";

import { Heading } from "components/base";
import { IconSpinner } from "components/icons";

import usePlayRadiusDefaults from "./hooks/usePlayRadiusDefaults";
import {
  RADIUS_SECTIONS,
  convertDimensionsToDisplay,
  getDimensionFields
} from "./models/GeoModelDefaults";

// Per-section pending edits: dimensionName → partial overrides
type PlayEdits = Record<string, Partial<PlayRadiusDimension>>;

// ---- Component ---------------------------------------------------------------

export function GeoModelDefaults() {
  const { defaults, isLoading, save, createAsync, remove } = usePlayRadiusDefaults();

  const { availablePlays } = useUserModules();
  const canUseGeoModels = useCanUseGeoModels();
  const { convert, convertBack, getDisplayUnit } = useLengthConversion();
  const dimensionFields = getDimensionFields(getDisplayUnit());
  // null = list view; string = play name being edited
  const [editingPlay, setEditingPlay] = useState<string | null>(null);
  const [addPlayVisible, setAddPlayVisible] = useState(false);
  const [playEdits, setPlayEdits] = useState<PlayEdits>({});
  const [isAddingPlays, setIsAddingPlays] = useState(false);
  const [form] = Form.useForm<{ plays: string[] }>();

  const editingEntry = editingPlay
    ? defaults.find((d) => d.play === editingPlay) ?? null
    : null;

  const hasChanges = useMemo(() => {
    if (!editingEntry) return false;
    for (const [sectionName, edits] of Object.entries(playEdits)) {
      const savedDimRaw = editingEntry.dimensions.find((d) => d.name === sectionName);
      const savedDim = savedDimRaw
        ? convertDimensionsToDisplay([savedDimRaw], (v) => convert(v, 2))[0]
        : undefined;
      for (const [key, value] of Object.entries(edits)) {
        const original = savedDim?.[key] ?? 0;
        if (value !== original) return true;
      }
    }
    return false;
  }, [playEdits, editingEntry, convert]);

  const dimensionFieldsByKey = useMemo(
    () => Object.fromEntries(dimensionFields.map((f) => [f.key, f])),
    [dimensionFields]
  );

  const editContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editingPlay) return;
    const frame = requestAnimationFrame(() => {
      const input = editContainerRef.current?.querySelector<HTMLInputElement>("input");
      if (input) {
        input.focus();
        input.select();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [editingPlay]);

  if (!canUseGeoModels) return null;

  const openEdit = (play: string) => {
    setPlayEdits({});
    setEditingPlay(play);
  };

  const closeEdit = () => {
    setPlayEdits({});
    setEditingPlay(null);
  };

  const setField = (dimensionName: string, field: string, value: number) => {
    setPlayEdits((prev) => ({
      ...prev,
      [dimensionName]: { ...prev[dimensionName], [field]: value }
    }));
  };

  const handleSaveEdit = () => {
    if (!editingEntry) return;
    // editingEntry.dimensions are the original DB values (base units).
    // playEdits values are in display units (typed by user).
    // Keep every original DB value as-is and only replace the fields the user
    // actually changed — so untouched fields never lose precision on a round-trip.
    const merged: PlayRadiusDefault = {
      play: editingEntry.play,
      dimensions: editingEntry.dimensions.map((dim) => {
        const edits = playEdits[dim.name];
        if (!edits) return dim;
        const updated: PlayRadiusDimension = { ...dim };
        for (const [key, value] of Object.entries(edits)) {
          if (typeof value !== "number") {
            updated[key] = value;
            continue;
          }
          // The DB value as it is displayed: convert to display unit, round to 2 dp.
          const originalDisplay =
            dim[key] != null ? convert(dim[key] as number, 2) : undefined;
          // Unchanged from what was displayed → keep the original DB value untouched.
          if (value === originalDisplay) continue;
          // Changed → convert the typed display value back to the base unit.
          // -1 precision stores it with full decimal places (no rounding).
          updated[key] = convertBack(value, -1);
        }
        return updated;
      })
    };
    save(merged);
    closeEdit();
  };

  const handleAddPlay = async () => {
    const { plays } = await form.validateFields();
    const newEntries: PlayRadiusDefault[] = plays
      .filter((play) => !defaults.some((d) => d.play === play))
      .map((play) => ({ play, dimensions: [] }));
    if (newEntries.length === 0) return;

    setIsAddingPlays(true);
    try {
      await createAsync(newEntries);
      form.resetFields();
      setAddPlayVisible(false);
    } finally {
      setIsAddingPlays(false);
    }
  };

  if (editingEntry) {
    return (
      <Container ref={editContainerRef}>
        <Button onClick={closeEdit} style={{ alignSelf: "flex-start" }}>
          &#8592; Back
        </Button>
        <Heading element="h4">{editingEntry.play}</Heading>

        {(editingEntry.dimensions.length > 0
          ? editingEntry.dimensions.map((d) => d.name)
          : RADIUS_SECTIONS
        ).map((sectionName) => {
          const savedDimRaw = editingEntry.dimensions.find((d) => d.name === sectionName);
          const edits = playEdits[sectionName] ?? {};

          // Use keys from the actual dim when available, else fall back to standard fields.
          // Standard keys are ordered Left/Right/Up/Down; unknown keys are appended after.
          const STANDARD_KEY_ORDER = [
            "leftWidth",
            "rightWidth",
            "upHeight",
            "downHeight"
          ];
          const sectionFields = savedDimRaw
            ? (() => {
                const dimKeys = Object.keys(savedDimRaw).filter((k) => k !== "name");
                const standard = STANDARD_KEY_ORDER.filter((k) => dimKeys.includes(k));
                const extra = dimKeys.filter((k) => !STANDARD_KEY_ORDER.includes(k));
                return [...standard, ...extra].map(
                  (k) =>
                    dimensionFieldsByKey[k] ?? {
                      key: k,
                      label: k,
                      unit: getDisplayUnit()
                    }
                );
              })()
            : dimensionFields;

          return (
            <SettingsSection key={sectionName}>
              <SectionTitle>{sectionName}</SectionTitle>
              <FieldGrid>
                {sectionFields.map(({ key, label, unit }) => (
                  <FieldRow key={key}>
                    <FieldLabel>
                      {label}
                      {unit ? <FieldUnit> ({unit})</FieldUnit> : null}
                    </FieldLabel>
                    <InputNumber
                      min={0}
                      value={
                        edits[key] !== undefined
                          ? edits[key]
                          : savedDimRaw?.[key] != null
                          ? convert(savedDimRaw[key] as number, 2)
                          : (savedDimRaw?.[key] as number)
                      }
                      onChange={(v) => setField(sectionName, key, v as number)}
                      style={{ width: 120 }}
                    />
                  </FieldRow>
                ))}
              </FieldGrid>
            </SettingsSection>
          );
        })}

        <SaveRow>
          <Button type="primary" onClick={handleSaveEdit} disabled={!hasChanges}>
            Save Changes
          </Button>
          <Button onClick={closeEdit}>Cancel</Button>
        </SaveRow>
      </Container>
    );
  }

  const configuredPlays = new Set(defaults.map((d) => d.play));
  const availableToAdd = availablePlays.filter((p) => !configuredPlays.has(p));

  const columns: ColumnsType<PlayRadiusDefault> = [
    {
      title: "Resource Play",
      dataIndex: "play",
      key: "play",
      render: (play: string) => <>{play}</>
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_: unknown, record: PlayRadiusDefault) => (
        <ButtonWrapper>
          <Tooltip title={`Edit config`} overlayInnerStyle={{ padding: "10px" }}>
            <Button
              style={{ margin: "1px" }}
              type="primary"
              icon={<Icon path={mdiPencil} size={1.4} />}
              onClick={() => openEdit(record.play)}
            />
          </Tooltip>
          <Popconfirm
            title={`Delete "${record.play}" defaults?`}
            onConfirm={() => remove(record.play)}
            okText="Delete"
            cancelText="Cancel">
            <Tooltip title={`Delete`} overlayInnerStyle={{ padding: "10px" }}>
              <Button
                style={{ margin: "1px" }}
                type="primary"
                danger
                icon={<Icon path={mdiDelete} size={1.4} />}
              />
            </Tooltip>
          </Popconfirm>
        </ButtonWrapper>
      )
    }
  ];

  return (
    <Container>
      <Header>
        <Heading element="h4">Geo Model Defaults</Heading>
        <Button
          type="primary"
          onClick={() => setAddPlayVisible(true)}
          disabled={isLoading}>
          + Add Play
        </Button>
      </Header>

      {isLoading ? (
        <SpinnerContainer>
          <IconSpinner />
        </SpinnerContainer>
      ) : (
        <Table dataSource={defaults} columns={columns} rowKey="play" />
      )}

      <Modal
        title="Add Play"
        open={addPlayVisible}
        onOk={handleAddPlay}
        onCancel={() => {
          form.resetFields();
          setAddPlayVisible(false);
        }}
        okText="Add"
        okButtonProps={{ loading: isAddingPlays }}
        cancelButtonProps={{ disabled: isAddingPlays }}>
        <Form form={form} layout="vertical">
          <Form.Item name="plays" label="Resource Plays">
            <Select
              mode="multiple"
              showSearch
              placeholder="Select one or mutiple plays"
              options={availableToAdd.map((p) => ({ label: p, value: p }))}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              maxTagCount="responsive"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Container>
  );
}

// ---- Styled components -------------------------------------------------------

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const SettingsSection = styled.div`
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 16px 20px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #444;
  margin-bottom: 12px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px 24px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  color: #555;
`;

const FieldUnit = styled.span`
  color: #999;
`;

const SaveRow = styled.div`
  display: flex;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  .ant-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
